import { ColorModeOptions } from '@gui/constants/ColorModeOptions';
import { Panels } from '@gui/constants/Panels';
import { ControllerManager } from '@gui/controller/ControllerManager';
import {
    DELETE_FROM_STORE,
    type AllColorScheme,
    type Camera,
    type ColorScheme,
    type DeepPartial,
    type GlobalDesignSchemeState,
    type SettingsState,
} from '@visualizer/types';
import { getLoadingStatus, LoadingStatus } from 'store/LoadingStore';
import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector, type StateStorage } from 'zustand/middleware';
import { selectColorScheme, selectNewColorScheme } from '../ColorScheme';
import { createGlobalState, GlobalActions, ResetTarget } from '../globalState';
import {
    createNewLayerState,
    DataLayerState,
    ElementLayerState,
    FiltersState,
    LayerActions,
    LayerManagementActions,
    LayerState,
    normalizeLayerState,
    PerformanceMetricsLayerState,
    ProjectionLayerState,
    SegmentsLayerState,
    SimulationItemsLayerState,
} from '../layerState';
import { buildEmptyLayerIdForGeodata, buildEmptyLayerIdForSimulationSet, hasRealSimulations } from '@data/emptySimulationIdentity';

import useNotificationStore from '../notification';
import { NotificationType } from '@visualizer/types';
import { getResourceStore } from 'store/ResourceStore';
import { cleanupPersistedState } from './stateCleanup';
import {
    PERSIST_STORE_VERSION,
    type DesignSchemesBucketState,
    type GlobalBucketState,
    type LayersBucketState,
    type PersistedStoreSlice,
    type PersistedBuckets,
    getDefaultDesignSchemesBucketState,
    getDefaultGlobalBucketState,
    getDefaultLayersBucketState,
} from './persistedBuckets';
import { createBucketPersistenceAdapter } from './bucketPersistenceAdapter';

export type PersistentState = {
    global: GlobalBucketState;
    layers: LayersBucketState;
    designSchemes: DesignSchemesBucketState;
};

const getDefaultState = (): PersistentState => {
    return {
        global: getDefaultGlobalBucketState(),
        layers: getDefaultLayersBucketState(),
        designSchemes: getDefaultDesignSchemesBucketState(),
    };
};

const resolveLayerGeodataId = (layerId: string): string => {
    if (!layerId || layerId === 'mixed') return '';

    const resource = getResourceStore();
    const gridResources = resource.status === 'loaded' ? resource.gridResources : null;
    if (!gridResources) return '';

    const matches = gridResources.geodatas.flatMap((geodata) =>
        geodata.simulations_sets.flatMap((simulationsSet) =>
            simulationsSet.simulations
                .filter((subSimulation) => `${simulationsSet.id}-${subSimulation.id}` === layerId)
                .map(() => geodata.id)
        )
    );

    if (matches.length === 1) return matches[0];

    const emptyMatches = gridResources.geodatas.flatMap((geodata) => {
        if (geodata.simulations_sets.length === 0) {
            return buildEmptyLayerIdForGeodata(geodata.id) === layerId ? [geodata.id] : [];
        }

        return geodata.simulations_sets.flatMap((simulationSet) =>
            !hasRealSimulations(simulationSet.simulations) && buildEmptyLayerIdForSimulationSet(simulationSet.id) === layerId
                ? [geodata.id]
                : []
        );
    });

    return emptyMatches.length === 1 ? emptyMatches[0] : '';
};

// Used to update only partial portions of nested items (e.g. solarParams.unitOfMeasure without updating the whole solarParams)
function deepMerge<T>(target: T, source: DeepPartial<T>): T {
    const output = { ...(target || {}) } as T;

    for (const key in source as any) {
        const srcVal = (source as any)[key];
        const tgtVal = (target as any)?.[key];

        //  1. Deletion marker logic
        if (srcVal === DELETE_FROM_STORE) {
            delete (output as any)[key];
            continue;
        }

        //  2. Recursive merge for objects
        if (srcVal && typeof srcVal === 'object' && !Array.isArray(srcVal)) {
            (output as any)[key] = deepMerge(tgtVal ?? {}, srcVal);
            continue;
        }

        //  3. Normal assignment (ignore undefined)
        if (srcVal !== undefined) {
            (output as any)[key] = srcVal;
        }
    }

    return output;
}

type DesignSchemesActions = {
    setDesignSchemes: (updates: DeepPartial<GlobalDesignSchemeState>) => void;
};

type Actions = GlobalActions & LayerActions & LayerManagementActions & DesignSchemesActions;

// Simple safe clone to avoid sharing references with defaults
function clone<T>(obj: T): T {
    if (typeof structuredClone === 'function') return structuredClone(obj);
    return JSON.parse(JSON.stringify(obj));
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isPersistedBuckets = (value: unknown): value is PersistedBuckets =>
    isRecord(value) && 'global' in value && 'layers' in value && 'designSchemes' in value;

const disabledStorage: StateStorage = {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
};

const persistentStorage = createBucketPersistenceAdapter(() => localStorage);
const ephemeralStorage = createJSONStorage<PersistedStoreSlice>(() => disabledStorage);
let persistenceEnabled = true;
const EMPTY_LAYER_STATE = createNewLayerState();

const usePersistentStore = create<PersistentState & Actions>()(
    subscribeWithSelector(
        persist(
            (set, get) => ({
                ...getDefaultState(),

                // --- ACTIONS ---

                setPanel: (updates, key) =>
                    set((state) => ({
                        ...state,
                        global: {
                            ...state.global,
                            [key]: deepMerge(state.global[key], updates),
                        },
                    })),
                setDesignSchemes: (updates) =>
                    set((state) => ({
                        ...state,
                        designSchemes: deepMerge(state.designSchemes, updates),
                    })),

                reset: (target: ResetTarget) => {
                    switch (target.scope) {
                        case 'store': {
                            set((state) => ({
                                ...state,
                                global: {
                                    ...getDefaultGlobalBucketState(),
                                    activeGridId: state.global.activeGridId,
                                },
                                layers: {
                                    ...state.layers,
                                    layerStates: Object.fromEntries(
                                        Object.entries(state.layers.layerStates || {}).map(([id, layerState]) => [
                                            id,
                                            clone(createNewLayerState(layerState?.geodataId ?? '')),
                                        ])
                                    ) as Record<string, LayerState>,
                                },
                                designSchemes: getDefaultDesignSchemesBucketState(),
                            }));

                            return;
                        }
                        case 'global': {
                            set((state) => ({
                                ...state,
                                global: {
                                    ...getDefaultGlobalBucketState(),
                                    activeGridId: state.global.activeGridId,
                                },
                            }));
                            return;
                        }
                        case 'globalPanel': {
                            const next = createGlobalState();
                            set((state) => ({
                                ...state,
                                global: {
                                    ...state.global,
                                    [target.panel]: clone(next[target.panel]),
                                },
                            }));
                            return;
                        }
                        case 'layer': {
                            const id = target.layerId ?? get().layers.activeLayerID;
                            if (!id) return;
                            set((state) => ({
                                ...state,
                                layers: {
                                    ...state.layers,
                                    layerStates: {
                                        ...state.layers.layerStates,
                                        [id]: clone(createNewLayerState(state.layers.layerStates[id]?.geodataId ?? '')),
                                    },
                                },
                            }));
                            return;
                        }
                        case 'layerPanel': {
                            const id = target.layerId ?? get().layers.activeLayerID;
                            if (!id) return;
                            const fresh = createNewLayerState(get().layers.layerStates[id]?.geodataId ?? '');
                            const slice = target.slice;
                            const sliceDefault = clone(fresh[slice]);
                            set((state) => {
                                const prev = state.layers.layerStates[id];
                                if (!prev) return state;
                                return {
                                    ...state,
                                    layers: {
                                        ...state.layers,
                                        layerStates: {
                                            ...state.layers.layerStates,
                                            [id]: {
                                                ...prev,
                                                [slice]: Array.isArray(sliceDefault) ? ([...sliceDefault] as any) : sliceDefault,
                                            },
                                        },
                                    },
                                };
                            });
                            return;
                        }
                    }
                },

                setDataLayer: (updates) =>
                    set((state) => {
                        const id = state.layers.activeLayerID;
                        if (!id) return state;
                        return {
                            ...state,
                            layers: {
                                ...state.layers,
                                layerStates: {
                                    ...state.layers.layerStates,
                                    [id]: {
                                        ...state.layers.layerStates[id],
                                        data: { ...state.layers.layerStates[id].data, ...updates },
                                    },
                                },
                            },
                        };
                    }),
                setPerformanceMetricsPanel: (updates) =>
                    set((state) => {
                        const id = state.layers.activeLayerID;
                        if (!id) return state;
                        return {
                            ...state,
                            layers: {
                                ...state.layers,
                                layerStates: {
                                    ...state.layers.layerStates,
                                    [id]: {
                                        ...state.layers.layerStates[id],
                                        performanceMetrics: {
                                            ...state.layers.layerStates[id].performanceMetrics,
                                            ...updates,
                                        } as PerformanceMetricsLayerState,
                                    },
                                },
                            },
                        };
                    }),
                // Updates only the 'projection' slice of the active layer
                setProjectionPanel: (updates) =>
                    set((state) => {
                        const id = state.layers.activeLayerID;
                        if (!id) return state;
                        return {
                            ...state,
                            layers: {
                                ...state.layers,
                                layerStates: {
                                    ...state.layers.layerStates,
                                    [id]: {
                                        ...state.layers.layerStates[id],
                                        projection: {
                                            ...state.layers.layerStates[id].projection,
                                            ...updates,
                                        },
                                    },
                                },
                            },
                        };
                    }),

                setElementPanel: (updates) =>
                    set((state) => {
                        const id = state.layers.activeLayerID;
                        if (!id) return state;
                        return {
                            ...state,
                            layers: {
                                ...state.layers,
                                layerStates: {
                                    ...state.layers.layerStates,
                                    [id]: {
                                        ...state.layers.layerStates[id],
                                        element: { ...state.layers.layerStates[id].element, ...updates },
                                    },
                                },
                            },
                        };
                    }),

                setSimulationItems: (updates) =>
                    set((state) => {
                        const id = state.layers.activeLayerID;
                        if (!id) return state;
                        return {
                            ...state,
                            layers: {
                                ...state.layers,
                                layerStates: {
                                    ...state.layers.layerStates,
                                    [id]: {
                                        ...state.layers.layerStates[id],
                                        simulationItems: { ...state.layers.layerStates[id].simulationItems, ...updates },
                                    },
                                },
                            },
                        };
                    }),

                setSegmentsPanel: (updates) =>
                    set((state) => {
                        const id = state.layers.activeLayerID;
                        if (!id) return state;
                        const currentLayer = state.layers.layerStates[id];
                        if (!currentLayer) return state;
                        const normalizedLayer = normalizeLayerState(currentLayer, currentLayer.geodataId || resolveLayerGeodataId(id));
                        return {
                            ...state,
                            layers: {
                                ...state.layers,
                                layerStates: {
                                    ...state.layers.layerStates,
                                    [id]: {
                                        ...normalizedLayer,
                                        segments: { ...normalizedLayer.segments, ...updates },
                                    },
                                },
                            },
                        };
                    }),
                addLayer: (id) => {
                    console.log('Adding layer', id);
                    set((state) => {
                        const existingLayer = state.layers.layerStates[id];
                        const resolvedGeodataId = resolveLayerGeodataId(id);
                        const geodataId = resolvedGeodataId || existingLayer?.geodataId || '';
                        const shouldSetActive = state.layers.activeLayerID === '' || state.layers.activeLayerID === undefined;

                        if (existingLayer) {
                            const needsNormalization = existingLayer.geodataId !== geodataId || !existingLayer.segments;

                            if (!shouldSetActive && !needsNormalization) {
                                return state;
                            }
                            const normalizedLayer = normalizeLayerState(existingLayer, geodataId);

                            return {
                                ...state,
                                layers: {
                                    ...state.layers,
                                    activeLayerID: shouldSetActive ? id : state.layers.activeLayerID,
                                    layerStates: {
                                        ...state.layers.layerStates,
                                        [id]: normalizedLayer,
                                    },
                                },
                            };
                        }

                        return {
                            ...state,
                            layers: {
                                ...state.layers,
                                activeLayerID: shouldSetActive ? id : state.layers.activeLayerID,
                                layerStates: {
                                    ...state.layers.layerStates,
                                    [id]: createNewLayerState(geodataId),
                                },
                            },
                        };
                    });
                },
                deleteLayer: (id) => {
                    set((state) => {
                        const { [id]: _, ...rest } = state.layers.layerStates;
                        const { [id]: __, ...restFailed } = state.layers.failedLayers;
                        return {
                            ...state,
                            layers: {
                                ...state.layers,
                                layerStates: rest,
                                failedLayers: restFailed,
                                activeLayerID: state.layers.activeLayerID === id ? Object.keys(rest)[0] : state.layers.activeLayerID,
                            },
                        };
                    });
                },

                setCurrentLayerId: (id) =>
                    set((state) => {
                        if (state.layers.failedLayers[id]) {
                            return state;
                        }
                        const nextLayerState = state.layers.layerStates[id] ?? createNewLayerState();
                        return {
                            ...state,
                            layers: {
                                ...state.layers,
                                layerStates: {
                                    ...state.layers.layerStates,
                                    [id]: nextLayerState,
                                },
                                activeLayerID: id,
                            },
                        };
                    }),

                markLayerFailed: (id: string, reason?: string) =>
                    set((state) => ({
                        ...state,
                        layers: {
                            ...state.layers,
                            failedLayers: {
                                ...state.layers.failedLayers,
                                [id]: reason ?? 'Layer loading failed',
                            },
                        },
                    })),

                clearLayerFailed: (id: string) =>
                    set((state) => {
                        if (!state.layers.failedLayers[id]) return state;
                        const { [id]: _, ...restFailed } = state.layers.failedLayers;
                        return {
                            ...state,
                            layers: {
                                ...state.layers,
                                failedLayers: restFailed,
                            },
                        };
                    }),

                isLayerFailed: (id: string) => !!get().layers.failedLayers[id],

                updateCurrentLayerState: (updates) => {
                    const currentId = get().layers.activeLayerID;
                    if (!currentId) return; // Do nothing if no layer is selected

                    set((state) => ({
                        layers: {
                            ...state.layers,
                            layerStates: {
                                ...state.layers.layerStates,
                                [currentId]: {
                                    ...state.layers.layerStates[currentId],
                                    ...updates,
                                },
                            },
                        },
                    }));
                },

                updateLayerState: (id: string, updates: DeepPartial<LayerState>) => {
                    const prev = get().layers.layerStates[id];
                    if (!prev) return;

                    set((state) => ({
                        layers: {
                            ...state.layers,
                            layerStates: {
                                ...state.layers.layerStates,
                                [id]: deepMerge(prev, updates),
                            },
                        },
                    }));
                },

                getActiveLayerState: () => {
                    const activeLayerId = get().layers.activeLayerID;
                    if (!activeLayerId) return EMPTY_LAYER_STATE;
                    return get().layers.layerStates[activeLayerId] ?? EMPTY_LAYER_STATE;
                },

                resetFilter: () => {
                    const activeLayerID = get().layers.activeLayerID;
                    if (!activeLayerID) return;

                    const initialFilter = createNewLayerState().filter;

                    set((state) => ({
                        layers: {
                            ...state.layers,
                            layerStates: {
                                ...state.layers.layerStates,
                                [activeLayerID]: {
                                    ...state.layers.layerStates[activeLayerID],
                                    filter: initialFilter,
                                },
                            },
                        },
                    }));
                },
                setCamera: (camera: Camera) => {
                    get().setPanel({ currentCamera: camera }, Panels.AMBIENT);
                },
            }),

            {
                name: '<grid-id>',
                version: PERSIST_STORE_VERSION,
                storage: persistentStorage,
                partialize: (state) => ({
                    global: state.global,
                    layers: state.layers,
                    designSchemes: state.designSchemes,
                }),
                merge: (persistedState: unknown, currentState: PersistentState & Actions): PersistentState & Actions => {
                    // Persist can pass `undefined` here on first load.
                    if (!persistedState) {
                        return currentState;
                    }
                    if (!isPersistedBuckets(persistedState)) {
                        return currentState;
                    }

                    const { state: cleanedState, notifications } = cleanupPersistedState(persistedState);

                    if (notifications.length > 0) {
                        queueMicrotask(() => {
                            const notifier = useNotificationStore.getState();
                            notifications.forEach((message) => notifier.showNotification(message, NotificationType.Info));
                        });
                    }

                    return {
                        ...currentState,
                        ...cleanedState,
                    };
                },
                onRehydrateStorage: () => {
                    // optional
                    return (state, error) => {
                        if (error) {
                            console.log('an error happened during hydration', error);
                            return;
                        }
                        if (!state) return;
                        console.warn('hydration started');
                        const hydratedLayers = state.layers?.layerStates || {};
                        const hydratedActiveId = state.layers?.activeLayerID || '';

                        try {
                            if (getLoadingStatus() === LoadingStatus.VISUALIZER_READY) {
                                ControllerManager.getInstance().getPipelineController().refreshFromStore(hydratedLayers);
                            }
                        } catch {
                            // swallow
                        }

                        console.log('hydration finished', { hydratedLayers, hydratedActiveId });
                    };
                },
            }
        )
    )
);

export default usePersistentStore;

export const setPersistenceEnabled = (enabled: boolean): void => {
    if (enabled === persistenceEnabled) return;
    persistenceEnabled = enabled;
    usePersistentStore.persist.setOptions({ storage: enabled ? persistentStorage : ephemeralStorage });
};

export const clearPersistedStateStorage = (storageName = usePersistentStore.persist.getOptions().name): void => {
    if (!storageName) return;
    localStorage.removeItem(storageName);
};

export const useGlobalBucket = <T>(selector: (state: GlobalBucketState) => T): T => usePersistentStore((state) => selector(state.global));
export const useLayersBucket = <T>(selector: (state: LayersBucketState) => T): T => usePersistentStore((state) => selector(state.layers));
export const useDesignSchemesBucket = <T>(selector: (state: DesignSchemesBucketState) => T): T =>
    usePersistentStore((state) => selector(state.designSchemes));

export const getGlobalState = (): GlobalBucketState => usePersistentStore.getState().global;
export const getLayersState = (): LayersBucketState => usePersistentStore.getState().layers;
export const getDesignSchemesState = (): DesignSchemesBucketState => usePersistentStore.getState().designSchemes;

export const getLayerState = (layerID: string): LayerState => {
    const layer = usePersistentStore.getState().layers.layerStates[layerID];
    return layer ?? createNewLayerState(resolveLayerGeodataId(layerID));
};
export const addLayer = (layerId: string): void => usePersistentStore.getState().addLayer(layerId);

export const removeLayer = (layerId: string): void => usePersistentStore.getState().deleteLayer(layerId);
export const markLayerFailed = (layerId: string, reason?: string): void => usePersistentStore.getState().markLayerFailed(layerId, reason);
export const clearLayerFailed = (layerId: string): void => usePersistentStore.getState().clearLayerFailed(layerId);
export const isLayerFailed = (layerId: string): boolean => usePersistentStore.getState().isLayerFailed(layerId);

export const getActiveLayerUID = (): string => usePersistentStore.getState().layers.activeLayerID;

export const getFilters = (): FiltersState => usePersistentStore.getState().getActiveLayerState().filter;

export const getSimulationItems = (): SimulationItemsLayerState => usePersistentStore.getState().getActiveLayerState().simulationItems;

export const getSettings = (): SettingsState => usePersistentStore.getState().global.settings;

export const getColorScheme = (): ColorScheme => selectColorScheme(getSettings().colorScheme as unknown as ColorModeOptions);

export const getNewColorScheme = (): AllColorScheme => selectNewColorScheme(getSettings().colorScheme as unknown as ColorModeOptions);

export const getProjection = (): ProjectionLayerState => usePersistentStore.getState()?.getActiveLayerState()?.projection;

export const getElement = (): ElementLayerState => usePersistentStore.getState()?.getActiveLayerState()?.element;

export const getVectorialScalarMode = (): 0 | 1 => usePersistentStore.getState()?.getActiveLayerState()?.VECTORIAL_SCALAR_MODE ?? 0;

export const getSegments = (): SegmentsLayerState => usePersistentStore.getState()?.getActiveLayerState()?.segments;

export const getDataLayer = (): DataLayerState => usePersistentStore.getState()?.getActiveLayerState()?.data;

export const reset = (target: ResetTarget): void => usePersistentStore.getState().reset(target);

export const setCurrentCamera = (camera: Camera): void => usePersistentStore.getState().setCamera(camera);
// Dev purpose only
// usePersistentStore.getState().global.data.isMixerMode = true;
